import React from 'react';
import cn from 'classnames';

import styles from './SlideShow.module.scss';

export interface SlideshowProps {
  slides: Array<JSX.Element>;
  className?: string;
  autoPlay?: boolean;
  delay?: number;
  dots?: boolean;
  dotsClassName?: string;
}

export const SlideShow: React.FC<SlideshowProps> = ({
  slides,
  className = '',
  autoPlay = true,
  delay = 2500,
  dots = true,
  dotsClassName = '',
}: SlideshowProps) => {
  const [index, setIndex] = React.useState(0);
  const timeoutRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  React.useEffect(() => {
    if (!autoPlay) return;
    resetTimeout();
    timeoutRef.current = setTimeout(
      () =>
        setIndex((prevIndex) =>
          prevIndex === slides.length - 1 ? 0 : prevIndex + 1
        ),
      delay
    );

    return () => {
      resetTimeout();
    };
  }, [index]);

  return (
    <div className={cn(styles.slideshow, className)}>
      <div
        className={styles.slideshowSlider}
        style={{ transform: `translate3d(${-index * 100}%, 0, 0)` }}
      >
        {slides.map((slide, index) => (
          <div key={index} className={styles.slide}>
            {slide}
          </div>
        ))}
      </div>

      {dots && (
        <div className={cn(styles.slideshowDots, dotsClassName)}>
          {slides.map((_, idx) => (
            <div
              key={idx}
              className={cn(styles.slideshowDot, {
                [styles.active]: index === idx,
              })}
              onClick={() => {
                setIndex(idx);
              }}
            ></div>
          ))}
        </div>
      )}
    </div>
  );
};
